import { apiFetch } from "@hub/ui/api";
import { useEffect, useState } from "react";

/** One row in the version picker. Both pages render this; neither owns the fetch. */
export interface ReleaseOption {
  tag: string;
  prerelease: boolean;
  buildable: boolean;
}

interface ReleasesResponse {
  releases: ReleaseOption[];
  unavailable?: string;
}

/**
 * Copy under the version picker once `/api/releases` has settled.
 *
 * `null` is a failed fetch — "we could not ask" — which must not read as an
 * empty catalog. A body with `unavailable` keeps the server's own sentence,
 * including when a cached list came back with it. An empty list with no such
 * sentence really is "nothing published yet". A loaded, non-empty list
 * returns null so each page keeps its own helper line.
 *
 * Exported so the decision can be tested without rendering: this repo has no
 * jsdom/happy-dom and no @testing-library/react.
 */
export function releasesPickerNote(
  body: { releases: readonly ReleaseOption[]; unavailable?: string } | null,
): string | null {
  if (!body) return "Could not load the release list.";
  if (body.unavailable) return body.unavailable;
  if (body.releases.length === 0) {
    return "No releases have been published yet — tag one to build an image.";
  }
  return null;
}

/**
 * Shared load for the version picker on Add instance and Instance detail.
 *
 * Separate from whatever else the page is loading on purpose: GitHub being
 * unreachable must not stop the form, or the instance itself, from rendering.
 * The note is what tells those two failures apart.
 */
export function useReleases(): {
  releases: ReleaseOption[];
  releasesNote: string | null;
} {
  const [releases, setReleases] = useState<ReleaseOption[]>([]);
  const [releasesNote, setReleasesNote] = useState<string | null>(null);

  useEffect(() => {
    apiFetch<ReleasesResponse>("/api/releases")
      .then((body) => {
        setReleases(body.releases);
        setReleasesNote(releasesPickerNote(body));
      })
      .catch(() => setReleasesNote(releasesPickerNote(null)));
  }, []);

  return { releases, releasesNote };
}
